import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Activity,
  ActivityType,
  CurrentPractice,
  PrimaryGoal,
  UserProfile,
} from '@movmania/core'
import { DEFAULT_WEEKLY_GOAL_MIN, estimateKcal } from '@movmania/core'
import { buildDemoHistory } from './seed.js'

export interface NewActivityInput {
  type: ActivityType
  durationMin: number
  startedAt: Date
  note?: string
}

export interface OnboardingInput {
  displayName: string
  favoriteActivities: ActivityType[]
  currentPractice: CurrentPractice
  primaryGoal: PrimaryGoal
  weeklyGoalMin: number
  birthYear?: number
  heightCm?: number
  weightKg?: number
}

interface State {
  onboarded: boolean
  profile: UserProfile | null
  activities: Activity[]
  referrals: number
}

interface Actions {
  completeOnboarding(input: OnboardingInput): void
  addActivity(input: NewActivityInput): Activity
  removeActivity(id: string): void
  setWeeklyGoal(minutes: number): void
  setFavorites(types: ActivityType[]): void
  loadDemoHistory(): void
  reset(): void
}

const initial: State = {
  onboarded: false,
  profile: null,
  activities: [],
  referrals: 0,
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `a-${Date.now()}-${Math.round(Math.random() * 1e6)}`
}

export const useStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initial,

      completeOnboarding: (input) =>
        set({
          onboarded: true,
          profile: {
            id: newId(),
            displayName: input.displayName,
            locale: 'fr',
            weeklyGoalMin: input.weeklyGoalMin || DEFAULT_WEEKLY_GOAL_MIN,
            favoriteActivities: input.favoriteActivities,
            currentPractice: input.currentPractice,
            primaryGoal: input.primaryGoal,
            ...(input.birthYear ? { birthYear: input.birthYear } : {}),
            ...(input.heightCm ? { heightCm: input.heightCm } : {}),
            ...(input.weightKg ? { weightKg: input.weightKg } : {}),
            createdAt: new Date().toISOString(),
          },
        }),

      addActivity: (input) => {
        const activity: Activity = {
          id: newId(),
          type: input.type,
          durationMin: input.durationMin,
          startedAt: input.startedAt.toISOString(),
          source: 'manual',
          ...(input.note ? { note: input.note } : {}),
        }
        activity.kcal = estimateKcal(activity, get().profile?.weightKg)
        set((state) => ({
          activities: [activity, ...state.activities].sort((a, b) =>
            a.startedAt < b.startedAt ? 1 : -1,
          ),
        }))
        return activity
      },

      removeActivity: (id) =>
        set((state) => ({ activities: state.activities.filter((a) => a.id !== id) })),

      setWeeklyGoal: (minutes) =>
        set((state) => ({
          profile: state.profile ? { ...state.profile, weeklyGoalMin: minutes } : null,
        })),

      setFavorites: (types) =>
        set((state) => ({
          profile: state.profile
            ? { ...state.profile, favoriteActivities: types }
            : null,
        })),

      loadDemoHistory: () => set({ activities: buildDemoHistory(), referrals: 2 }),

      reset: () => set({ ...initial }),
    }),
    {
      name: 'movmania.prototype.v3',
      version: 3,
    },
  ),
)
